import React, { useRef, useEffect, useMemo } from "react";
import ForceGraph2D from "react-force-graph-2d";
import * as d3 from "d3";

export default function ForceGraph({ leakers, results }) {
  const fgRef = useRef();

  const graphData = useMemo(() => {
    const nodes = [];
    const links = [];
    const addedNodes = new Set();

    // Helper to add node if not exists
    const addNode = (id, name, group, metadata = {}) => {
      if (!addedNodes.has(id)) {
        nodes.push({ id, name, group, ...metadata });
        addedNodes.add(id);
      }
    };

    // Helper to calculate relevance score between leaker and article
    const calculateRelevance = (leaker, article) => {
      let score = 0;
      const leakerText = [
        leaker.name,
        leaker.role || leaker.occupation,
        leaker.access,
        leaker.notes || leaker.note,
        leaker.motivation
      ].filter(Boolean).join(' ').toLowerCase();

      const articleText = [
        article.themes,
        article.persons,
        article.organizations,
        article.explanation,
        article.title
      ].filter(Boolean).join(' ').toLowerCase();

      // Keyword matching
      const keywords = leakerText.split(/\W+/).filter(w => w.length > 3);
      keywords.forEach(kw => {
        if (articleText.includes(kw)) score += 1;
      });

      // Boost for specific matches
      if (articleText.includes('leak') || articleText.includes('classified')) score += 2;
      if (leaker.access && articleText.includes(leaker.access.split(' ')[0]?.toLowerCase())) score += 3;

      return score;
    };

    // Add central query node
    addNode('query', 'Query Input', 0, { size: 15 });

    // Add leaker nodes
    leakers.forEach((l, i) => {
      const lid = `leaker-${i}`;
      addNode(lid, l.name, 1, {
        size: 12,
        role: l.role || l.occupation,
        motivation: l.motivation
      });
      links.push({ source: 'query', target: lid, strength: 0.8 });
    });

    // Add article nodes
    results.forEach((r, i) => {
      const aid = `article-${i}`;
      const similarity = r.similarity || 0;
      addNode(aid, r.title?.substring(0, 40) || `Article ${i + 1}`, 2, {
        size: 6 + similarity * 10,
        similarity,
        url: r.url
      });

      // Link to query based on similarity
      if (similarity > 0.3) {
        links.push({
          source: 'query',
          target: aid,
          strength: similarity,
          color: similarity > 0.7 ? '#ef4444' : similarity > 0.5 ? '#f97316' : '#3b82f6'
        });
      }

      // Link leakers to articles based on relevance
      leakers.forEach((l, li) => {
        const relevance = calculateRelevance(l, r);
        if (relevance >= 3) {
          links.push({
            source: `leaker-${li}`,
            target: aid,
            strength: Math.min(relevance / 10, 1),
            color: relevance >= 5 ? '#ef4444' : '#eab308'
          });
        }
      });

      // Add publication node
      const source = r.source || r.publication;
      if (source) {
        const pubId = `pub-${source}`;
        addNode(pubId, source, 3, { size: 8 });
        links.push({ source: aid, target: pubId, strength: 0.5, color: '#6b7280' });
      }

      // Add journalist nodes (persons)
      if (r.persons) {
        const people = r.persons.split(/[,;]/).map(p => p.trim()).filter(Boolean).slice(0, 3);
        people.forEach(p => {
          const personId = `person-${p}`;
          addNode(personId, p, 4, { size: 6 });
          links.push({ source: aid, target: personId, strength: 0.4, color: '#a855f7' });
        });
      }

      // Add theme connections between articles
      if (r.themes) {
        const themes = r.themes.split(';').map(t => t.trim()).filter(Boolean).slice(0, 3);
        themes.forEach(theme => {
          const themeId = `theme-${theme}`;
          addNode(themeId, theme, 5, { size: 5 });
          links.push({ source: aid, target: themeId, strength: 0.3, color: '#22d3ee' });
        });
      }
    });

    return { nodes, links };
  }, [leakers, results]);

  useEffect(() => {
    if (fgRef.current) {
      const fg = fgRef.current;
      fg.d3Force("charge").strength(-200);
      fg.d3Force("link").distance(link => 100 / (link.strength || 0.5));
      fg.d3Force("center", d3.forceCenter(0, 0));
      fg.d3Force("collision", d3.forceCollide(30));
    }
  }, [results, leakers]);

  const colors = {
    0: "#ffffff", // query (white)
    1: "#3b82f6", // leakers (blue)
    2: "#4ade80", // articles (green)
    3: "#f97316", // publications (orange)
    4: "#a855f7", // journalists (purple)
    5: "#22d3ee"  // themes (cyan)
  };

  return (
    <div className="border border-gray-600 rounded p-4 mt-10 bg-gray-900">
      <h2 className="text-xl font-semibold mb-2 text-green-400 font-mono">
        Relationship Network Graph
      </h2>
      <p className="text-sm text-gray-400 mb-4 font-mono">
        Interactive network showing connections between query, leakers, articles, publications, journalists, and themes.
        Drag nodes to explore. Line thickness = connection strength.
      </p>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4 text-sm font-mono">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{backgroundColor: colors[0]}}></span>
          <span className="text-gray-400">Query</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{backgroundColor: colors[1]}}></span>
          <span className="text-gray-400">Leakers</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{backgroundColor: colors[2]}}></span>
          <span className="text-gray-400">Articles</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{backgroundColor: colors[3]}}></span>
          <span className="text-gray-400">Publications</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{backgroundColor: colors[4]}}></span>
          <span className="text-gray-400">Journalists</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full" style={{backgroundColor: colors[5]}}></span>
          <span className="text-gray-400">Themes</span>
        </span>
      </div>

      <div className="w-full h-[600px] overflow-hidden relative bg-gray-950 rounded">
        <ForceGraph2D
          ref={fgRef}
          graphData={graphData}
          enableNodeDrag={true}
          enableZoomPanInteraction={true}
          cooldownTicks={300}
          nodeLabel={node => `${node.name}${node.similarity ? ` (${Math.round(node.similarity * 100)}%)` : ''}`}
          linkWidth={link => (link.strength || 0.5) * 3}
          linkColor={link => link.color || '#4b5563'}
          nodeCanvasObject={(node, ctx, globalScale) => {
            const label = node.name;
            const fontSize = Math.max(10 / globalScale, 3);
            const size = node.size || 5;

            // Draw node
            ctx.fillStyle = colors[node.group] || "#fff";
            ctx.beginPath();
            ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
            ctx.fill();

            // Draw border for query node
            if (node.group === 0) {
              ctx.strokeStyle = '#4ade80';
              ctx.lineWidth = 2;
              ctx.stroke();
            }

            // Draw label
            ctx.font = `${fontSize}px monospace`;
            ctx.fillStyle = "#4ade80";
            ctx.textAlign = "center";
            ctx.fillText(label, node.x, node.y + size + fontSize + 2);
          }}
          onNodeClick={(node) => {
            if (node.url) {
              window.open(node.url, '_blank');
            }
          }}
        />
      </div>
    </div>
  );
}